import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Edit3, Trash2, Save, Loader2, X, Image as ImageIcon } from "lucide-react";
import AdminShell from "../components/AdminShell";
import ImageUploader from "../components/ImageUploader";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

const EMPTY = {
  name: "", logo_url: "", website: "", description: "",
  category: "", contact_email: "", contact_phone: "",
  language: "vi", notes: "",
};

export default function AdminSponsorLibrary() {
  const { lang } = useI18n();
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const L = (vi, en) => (lang === "en" ? en : vi);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [sponsors, setSponsors] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2000); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sponsors", { headers, params: q ? { q } : {} });
      setSponsors(r.data.sponsors || []);
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/admin/sponsors/${id}`, payload, { headers });
      else await api.post("/admin/sponsors", payload, { headers });
      setEditing(null);
      flash(L("Đã lưu", "Saved"));
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/sponsors/${id}`, { headers });
      setConfirmDelete(null);
      flash(L("Đã xóa", "Deleted"));
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  return (
    <AdminShell
      testid="admin-sponsor-library"
      subtitle={L("Thư Viện", "Library")}
      title={L("Nhà Tài Trợ Toàn Cầu", "Global Sponsors")}
      actions={
        <button type="button" onClick={() => setEditing({ mode: "create", data: { ...EMPTY } })} data-testid="sponsor-lib-add-btn"
          className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2 text-sm font-medium hover:bg-espresso-hover">
          <Plus className="w-4 h-4" /> {L("Thêm Nhà Tài Trợ", "Add Sponsor")}
        </button>
      }
    >
      <div className="max-w-[1200px]">
        {toast && <div data-testid="sponsor-lib-toast" className="mb-4 inline-block bg-[#E1EDE6] text-[#2D4A3E] rounded-xl px-4 py-2 text-sm font-medium">{toast}</div>}

        <div className="bg-white border border-espresso/10 rounded-3xl p-4 md:p-5 mb-6">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-3">
            <Search className="w-4 h-4 text-espresso-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Tìm theo tên, danh mục, mô tả...", "Search by name, category, description...")} data-testid="sponsor-lib-search"
              className="flex-1 bg-transparent text-sm focus:outline-none" />
            <button type="submit" data-testid="sponsor-lib-search-btn" className="text-xs uppercase tracking-[0.22em] text-espresso font-semibold">{L("Tìm", "Search")}</button>
          </form>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-espresso-muted" /></div>
        ) : sponsors.length === 0 ? (
          <div className="bg-white border border-dashed border-espresso/20 rounded-3xl p-12 text-center text-espresso-muted">
            {L("Chưa có nhà tài trợ nào trong thư viện.", "No sponsors in the library yet.")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sponsors.map((sp) => (
              <div key={sp.id} data-testid={`sponsor-lib-card-${sp.id}`} className="bg-white border border-espresso/10 rounded-3xl p-5 flex flex-col gap-3">
                <div className="aspect-[3/2] bg-linen-100 rounded-2xl overflow-hidden flex items-center justify-center">
                  {sp.logo_url ? <img src={sp.logo_url} alt={sp.name} className="max-h-full max-w-full object-contain" /> : <ImageIcon className="w-6 h-6 text-espresso-muted" />}
                </div>
                <div className="font-serif text-lg text-espresso">{sp.name}</div>
                {sp.category && <div className="text-[10px] uppercase tracking-[0.18em] text-emerald2 font-semibold">{sp.category}</div>}
                {sp.description && <p className="text-sm text-espresso-soft line-clamp-2">{sp.description}</p>}
                <div className="text-[10px] uppercase tracking-[0.18em] text-espresso-muted font-semibold">{sp.event_count || 0} {L("sự kiện", "events")}</div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setEditing({ mode: "edit", data: sp })} data-testid={`sponsor-lib-edit-${sp.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(sp)} data-testid={`sponsor-lib-delete-${sp.id}`} className="p-1.5 rounded-lg hover:bg-[#F5DADA] text-espresso-soft hover:text-[#7A2E2E]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <SponsorEditDialog mode={editing.mode} initial={editing.data} onClose={() => setEditing(null)}
          onSave={(payload) => save(payload, editing.mode === "edit" ? editing.data.id : null)} L={L} />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={L("Xóa nhà tài trợ?", "Delete sponsor?")}
          body={L("Mọi liên kết sự kiện cũng sẽ bị xóa. Tiếp tục?", "All event links will be removed. Continue?")}
          onCancel={() => setConfirmDelete(null)} onConfirm={() => remove(confirmDelete.id)} L={L}
        />
      )}
    </AdminShell>
  );
}

function SponsorEditDialog({ mode, initial, onClose, onSave, L }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return;
    setSaving(true);
    await onSave({
      name: form.name.trim(),
      logo_url: form.logo_url || null, website: form.website || null,
      description: form.description || null, category: form.category || null,
      contact_email: form.contact_email || null, contact_phone: form.contact_phone || null,
      language: form.language || "vi", notes: form.notes || null,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="sponsor-lib-dialog">
      <form onSubmit={submit} className="w-full max-w-2xl bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] max-h-[92vh] flex flex-col">
        <div className="px-6 md:px-8 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl md:text-3xl text-charcoal">{mode === "edit" ? L("Chỉnh sửa Nhà Tài Trợ", "Edit Sponsor") : L("Thêm Nhà Tài Trợ", "Add Sponsor")}</h3>
          <button type="button" onClick={onClose} data-testid="sponsor-lib-dialog-close" className="text-warmgray-500 hover:text-charcoal"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 md:px-8 py-6 overflow-y-auto grid md:grid-cols-2 gap-4">
          <Input label={L("Tên nhà tài trợ *", "Sponsor name *")} value={form.name} onChange={(v) => set("name", v)} testId="sponsor-lib-name" required />
          <Input label="Website" value={form.website} onChange={(v) => set("website", v)} testId="sponsor-lib-website" />
          <div className="md:col-span-2">
            <ImageUploader
              label={L("Logo nhà tài trợ", "Sponsor logo")}
              value={form.logo_url}
              onChange={(url) => set("logo_url", url)}
              subfolder="sponsors"
              aspect="landscape"
              testId="sponsor-lib-logo"
              hint={L("Logo nền trong suốt (PNG) hiển thị đẹp nhất. Tối đa 10MB.", "Transparent PNG works best. Max 10MB.")}
            />
          </div>
          <Input label={L("Danh mục", "Category")} value={form.category} onChange={(v) => set("category", v)} placeholder={L("VD: POS, Đào tạo, Pháp lý...", "e.g. POS, Education, Legal...")} testId="sponsor-lib-category" />
          <Textarea label={L("Mô tả", "Description")} value={form.description} onChange={(v) => set("description", v)} rows={3} testId="sponsor-lib-description" className="md:col-span-2" />
          <Input label={L("Email liên hệ", "Contact email")} value={form.contact_email} onChange={(v) => set("contact_email", v)} testId="sponsor-lib-contact-email" />
          <Input label={L("Điện thoại", "Contact phone")} value={form.contact_phone} onChange={(v) => set("contact_phone", v)} testId="sponsor-lib-contact-phone" />
          <Textarea label={L("Ghi chú nội bộ", "Internal notes")} value={form.notes} onChange={(v) => set("notes", v)} rows={2} testId="sponsor-lib-notes" className="md:col-span-2" />
        </div>
        <div className="px-6 md:px-8 py-4 border-t border-warmgray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal" data-testid="sponsor-lib-dialog-cancel">{L("Hủy", "Cancel")}</button>
          <button type="submit" disabled={!form.name?.trim() || saving} data-testid="sponsor-lib-dialog-save" className="inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-5 py-2 text-sm font-medium hover:bg-espresso-hover disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {L("Lưu", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConfirmDialog({ title, body, onCancel, onConfirm, L }) {
  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/40 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6" data-testid="confirm-dialog">
        <h3 className="font-serif text-2xl text-charcoal">{title}</h3>
        <p className="mt-2 text-sm text-warmgray-700">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} data-testid="confirm-cancel" className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal">{L("Hủy", "Cancel")}</button>
          <button type="button" onClick={onConfirm} data-testid="confirm-confirm" className="bg-[#7A2E2E] text-white rounded-full px-4 py-2 text-sm font-medium hover:opacity-90">{L("Xóa", "Delete")}</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testId, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}
